// Compact + full progress widgets for Phase D real-time scan progress.
// Backend ships `progress` on RunListItem and RunDetailMeta as:
//   { phase, detail?, current?, total?, at }

import { Activity } from 'lucide-react';

const PHASE_LABEL = {
  'crawler.start': 'Crawling',
  'crawler.done': 'Crawl done',
  'nuclei.start': 'Nuclei starting',
  'nuclei.tick': 'Nuclei',
  'nuclei.done': 'Nuclei done',
  'nuclei.dast.start': 'DAST starting',
  'nuclei.dast.tick': 'DAST',
  'nuclei.dast.done': 'DAST done',
};

function shortLabel(phase) {
  if (!phase) return 'Working';
  return PHASE_LABEL[phase] || phase;
}

function percent(p) {
  if (p == null) return null;
  if (p.total && p.total > 0 && typeof p.current === 'number') {
    return Math.min(100, Math.max(0, Math.round((p.current / p.total) * 100)));
  }
  return null;
}

// Tiny inline pill used in the Runs table row, under the status badge.
export function RunProgressPill({ progress }) {
  if (!progress || !progress.phase) return null;
  const pct = percent(progress);
  const counter =
    typeof progress.current === 'number' && typeof progress.total === 'number'
      ? `${progress.current.toLocaleString()}/${progress.total.toLocaleString()}`
      : null;
  return (
    <span className="inline-flex items-center gap-1 text-[10px] text-cyan-700/90 mt-0.5">
      <Activity className="w-2.5 h-2.5" />
      <span className="font-mono">{shortLabel(progress.phase)}</span>
      {counter && <span className="text-slate-500">{counter}</span>}
      {pct != null && <span className="text-slate-500">({pct}%)</span>}
    </span>
  );
}

// Full one-line detail row used on the run-detail page header.
export function RunProgressLine({ progress }) {
  if (!progress || !progress.phase) return null;
  const pct = percent(progress);
  const detail = progress.detail || '';
  return (
    <div className="mt-3 rounded-lg border border-cyan-300 bg-cyan-100 px-3 py-2 text-sm text-cyan-700 flex items-center gap-2">
      <Activity className="w-3.5 h-3.5 shrink-0" />
      <span className="font-mono text-xs uppercase tracking-wider text-cyan-700/80 shrink-0">
        {shortLabel(progress.phase)}
      </span>
      {detail && <span className="text-slate-800 break-all">{detail}</span>}
      {pct != null && <span className="ml-auto text-xs text-cyan-700 font-mono shrink-0">{pct}%</span>}
    </div>
  );
}
